/**
 * OkidVerificarModal — verificación de identidad OKID (RENAP + OCR DPI + facial).
 * Sube DPI (frente) + rostro (selfie) al bucket kyc y llama al edge okid-verificar.
 * Muestra saldo, resultado y el PDF de evidencia.
 */
import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj = any

export function OkidVerificarModal({ asociadoId, asociadoNombre, cui, onClose }: {
  asociadoId: string; asociadoNombre: string; cui?: string | null; onClose: () => void
}) {
  const [saldo, setSaldo] = useState<string>('…')
  const [dpiFile, setDpiFile] = useState<File | null>(null)
  const [frontFile, setFrontFile] = useState<File | null>(null)
  const [working, setWorking] = useState(false)
  const [result, setResult] = useState<AnyObj>(null)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    supabase.functions.invoke('okid-verificar', { body: { action: 'saldo' } }).then(({ data, error }) => {
      if (error) { setSaldo('error'); return }
      const acc = (data as AnyObj)?.data?.account ?? {}
      const s = acc.saldo ?? acc.balance ?? acc.credits ?? (data as AnyObj)?.data?.saldo
      setSaldo(s != null ? String(s) : ((data as AnyObj)?.ok ? 'OK' : 'error'))
    }).catch(() => setSaldo('error'))
  }, [])

  const verificar = async () => {
    if (!dpiFile || !frontFile) { setErr('Subí el DPI (frente) y el rostro'); return }
    setWorking(true); setErr(null); setResult(null); setPdfUrl(null)
    const stamp = Date.now()
    const dpiPath = `okid/${asociadoId}/${stamp}-dpi.jpg`
    const frontPath = `okid/${asociadoId}/${stamp}-front.jpg`
    const up1 = await supabase.storage.from('kyc').upload(dpiPath, dpiFile, { upsert: true, contentType: dpiFile.type })
    const up2 = await supabase.storage.from('kyc').upload(frontPath, frontFile, { upsert: true, contentType: frontFile.type })
    if (up1.error || up2.error) { setErr('No se pudieron subir las imágenes'); setWorking(false); return }
    const { data, error } = await supabase.functions.invoke('okid-verificar', {
      body: { asociado_id: asociadoId, dpi_path: dpiPath, front_path: frontPath, cui: cui ?? undefined },
    })
    setWorking(false)
    if (error) { setErr(error.message); return }
    setResult(data)
    const pp = (data as AnyObj)?.pdf_path
    if (pp) {
      const { data: signed } = await supabase.storage.from('okid').createSignedUrl(pp, 3600)
      if (signed?.signedUrl) setPdfUrl(signed.signedUrl)
    }
  }

  const box: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--ink-soft)' }
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--surface)', borderRadius: 14, width: 440, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 20, border: '1px solid var(--hairline)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Verificar identidad · OKID</h3>
          <button onClick={onClose} style={{ all: 'unset', cursor: 'pointer', color: 'var(--muted-foreground)', fontSize: 16, fontWeight: 700 }}>✕</button>
        </div>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--muted-foreground)' }}>
          {asociadoNombre}{cui ? ` · CUI ${cui}` : ''} · RENAP + OCR DPI + facial
        </p>

        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 99, background: 'var(--brand-50)', color: 'var(--brand-700, var(--brand))', marginBottom: 14 }}>
          Saldo OKID: {saldo}
        </div>

        {!result && (
          <div style={{ display: 'grid', gap: 12 }}>
            <label style={box}>
              DPI (frente) *
              <input type="file" accept="image/*" capture="environment" onChange={(e) => setDpiFile(e.target.files?.[0] ?? null)} />
            </label>
            <label style={box}>
              Rostro / selfie *
              <input type="file" accept="image/*" capture="user" onChange={(e) => setFrontFile(e.target.files?.[0] ?? null)} />
            </label>
            {err && <div style={{ fontSize: 12, color: 'var(--danger, #a32d2d)' }}>{err}</div>}
            <button className="btn btn-primary" onClick={verificar} disabled={working || !dpiFile || !frontFile}>
              {working ? 'Verificando…' : 'Verificar contra RENAP'}
            </button>
            <p style={{ margin: 0, fontSize: 11, color: 'var(--muted-foreground)' }}>Consume 1 crédito solo si la verificación se completa.</p>
          </div>
        )}

        {result && (
          <div style={{ display: 'grid', gap: 10 }}>
            <Row k="Estado" v={result.estado ?? '—'} tone={result.estado === 'ok' ? 'ok' : 'warn'} />
            {result.verification_id && <Row k="ID verificación" v={String(result.verification_id)} />}
            {result.resultado?.renap_encontrado != null && <Row k="RENAP" v={result.resultado.renap_encontrado ? '✓ encontrado' : '✗ no encontrado'} tone={result.resultado.renap_encontrado ? 'ok' : 'warn'} />}
            {result.resultado?.facial_match != null && <Row k="Rostro coincide" v={result.resultado.facial_match ? '✓ sí' : '✗ no'} tone={result.resultado.facial_match ? 'ok' : 'warn'} />}
            {pdfUrl && <a href={pdfUrl} target="_blank" rel="noreferrer" className="btn btn-ghost" style={{ textAlign: 'center' }}>Ver PDF de evidencia →</a>}
            <details style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
              <summary style={{ cursor: 'pointer' }}>Respuesta completa</summary>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 10 }}>{JSON.stringify(result.resultado, null, 2)}</pre>
            </details>
            <button className="btn btn-ghost" onClick={() => { setResult(null); setPdfUrl(null) }}>Nueva verificación</button>
          </div>
        )}
      </div>
    </div>
  )
}

function Row({ k, v, tone }: { k: string; v: string; tone?: 'ok' | 'warn' }) {
  const color = tone === 'ok' ? 'var(--ok-ink, #0f8a57)' : tone === 'warn' ? 'var(--warning, #d97706)' : 'var(--ink)'
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
      <span style={{ color: 'var(--muted-foreground)', fontWeight: 600 }}>{k}</span>
      <span style={{ fontWeight: 700, color }}>{v}</span>
    </div>
  )
}
